import { useContext } from "react";
import { toast } from "react-toastify";
import { FieldState, genUid } from "../CustomForm/common";
import CustomFieldContext from "../context/CustomFieldContext";
import { FIELD_ACTIVITY_STATUS, FIELD_PLAYCARD_STATUS, FIELD_TASK_STATUS } from "constant";

const basicState = {
  defaultValue: "",
  passValidate: false,
  label: {
    name: "label",
    title: "Field Name",
    placeholder: "Please enter field name.",
    value: "",
  },
  placeholder: {
    name: "placeholder",
    title: "Hint Text",
    placeholder: "Please enter hint text.",
    value: "",
  },
  rules: {
    required: {
      name: "required",
      title: "Field Type",
      value: "n",
      message: "This field is required.",
    },
    maxLength: {
      name: "maxLength",
      title: "Max Length",
      value: "50",
      message: "",
    },
  },
  conditions: [],
  dependsBy: [],
  visible: true,
};

const initState = (fieldType: string) => {
  let fieldState: FieldState;

  switch (fieldType) {
    case "input-text":
      fieldState = {
        ...basicState,
        id: genUid("field"),
        fieldType: "input-text",
        fieldSubType: "text-input",
        rules: {
          ...basicState.rules,
          maxLength: {
            ...basicState.rules.maxLength,
            value: "200",
          },
        },
      };
      break;
    case "drop-down":
      fieldState = {
        ...basicState,
        id: genUid("field"),
        fieldType: fieldType,
        fieldSubType: "single",
        isMultiple: false,
        options: [{ optionId: genUid("option"), optionValue: "" }],
      };
      break;
    case "date-picker":
      fieldState = {
        ...basicState,
        id: genUid("field"),
        fieldType: "date-picker",
        fieldSubType: "date-picker",
        rules: {
          ...basicState.rules,
        },
      };
      break;
    case "markdown":
      fieldState = {
        ...basicState,
        id: genUid("field"),
        fieldType: "markdown",
        fieldSubType: "markdown",
      };
      break;
    default:
      fieldState = {
        ...basicState,
        id: genUid("field"),
        fieldType: "none",
        fieldSubType: "none",
      };
      break;
  }

  return fieldState;
};

const types = [FIELD_ACTIVITY_STATUS, FIELD_TASK_STATUS, FIELD_PLAYCARD_STATUS];

interface FieldCustomizeProps {
  fieldType?: string;
}

const useFieldCustomize = () => {
  const { fieldStateList, setFieldStateList, currentFieldId, setCurrentFieldId } =
    useContext(CustomFieldContext);

  /**
   * ================================================================
   *                   Common Field Operations
   * ================================================================
   */

  /**
   * Add a new field into custom fields section when click / drag field icons from the left panel.
   * Before add a new field, all the current fields must pass the validation.
   * @param fieldType: Field Type
   */
  const addField = (fieldType: string) => {
    const { passValidate, id } = fieldStateList.find((s) => !s.passValidate) ?? {
      passValidate: true,
      id: "",
    };

    if (passValidate) {
      const fieldState = initState(fieldType);
      setFieldStateList([...fieldStateList, fieldState]);
      setCurrentFieldId(fieldState.id);
    } else {
      setCurrentFieldId(id);
    }
  };

  /**
   * setup the current field id when a field is selected.
   * @param id: Field id, suffix with `field_`
   */
  const selectField = (id: string) => {
    if (!types.includes(id)) {
      setCurrentFieldId(id);
    }
  };

  const getFieldLabels = (ids: string[]) => {
    return fieldStateList.filter((s) => ids.includes(s.id)).map((s) => s.label.value);
  };

  /**
   * clear the current field id and update `fieldStateList` list when remove a field.
   * @param id: Field id, suffix with `field_`
   */
  const removeField = (id: string) => {
    const state = getFieldState(id);

    const dependsBy = state?.dependsBy ?? [];
    // if fieldid in the dependsby no longer exists in fieldstatelist, ignore it.
    const isDepends = fieldStateList.filter((f) => dependsBy.includes(f.id)).length > 0;
    if (isDepends) {
      const fieldLabels = getFieldLabels(dependsBy);

      toast.error(
        `Field ${state?.label.value} depends by field(s) ${fieldLabels.join(
          ","
        )}, please reset coresponding field(s) before remove field.`
      );
      return false;
    } else {
      // when remove field with condition rule, update the dependsby field.
      const conditions = state?.conditions ?? [];
      setCurrentFieldId("");
      setFieldStateList((items) =>
        items
          .filter((item) => item.id !== id)
          .map((item) =>
            conditions.map((c) => c.fieldId).includes(item.id)
              ? {
                  ...item,
                  dependsBy: [...(item.dependsBy ?? []).filter((i) => i !== id)],
                }
              : item
          )
      );
    }
  };

  /**
   * used for style.
   * @param id: Field id, suffix with `field_`
   * @returns boolean
   */
  const isCurrentField = (id: string) => {
    return currentFieldId === id;
  };

  const getFieldState = (id: string) => {
    return fieldStateList.find((x) => x.id === id);
  };

  const getConditionOptions = (fieldId: string, ruleId: string) => {
    const state = getFieldState(fieldId);
    // get all drop down fields and exclude itself if it is a drop down field.
    const comboList = fieldStateList
      .filter((field) => field.fieldType === "drop-down")
      .filter((field) => (state?.fieldType === "drop-down" ? field.id !== fieldId : true));
    const optionList = comboList.filter(
      (combo) =>
        !(state?.conditions ?? [])
          .filter((c) => c.ruleId !== ruleId)
          .map((c) => c.fieldId)
          .includes(combo.id)
    );
    return optionList;
  };

  const isConditionVisiable = (fieldId: string) => {
    const optionList = getConditionOptions(fieldId, "");
    return optionList.length > 0;
  };

  return {
    currentFieldId,
    fieldStateList,
    setFieldStateList,
    addField,
    selectField,
    removeField,
    isCurrentField,
    getConditionOptions,
    isConditionVisiable,
    getFieldState,
  };
};

export default useFieldCustomize;
